/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import ChartPeriodSelector, { ChartPeriodId } from "components/charts/modern/inputs/ChartPeriodSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import type { ColorTheme } from "components/charts/modern/quotes/getColorThemeForIndex";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";
import type Highcharts from "highcharts/highstock";
import type { DashStyleValue, SeriesColumnOptions, YAxisPlotLinesOptions } from "highcharts/highstock";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type {
  IQuotesHistoryQuery,
  IQuotesHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/QuotesHistoryQuery.generated";
import QuotesHistoryQuery from "lib/graphql/queries/QuotesHistoryQuery";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import flatten from "lodash/flatten";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface TradesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function TradesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: TradesChartProps) {
  const intl = useIntl();
  const issuer = useRootIssuer();
  const theme = useTheme();

  const isAmericanCompany = issuer.__typename === "AmericanCompanyIssuer";

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = theme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  // Series colors for theme
  const seriesColor = theme === "dark" ? colors.sky[900] : colors.sky[50];
  const seriesColorShortPeriod = theme === "dark" ? colors.sky[700] : colors.sky[300];
  const seriesBorderColor = theme === "dark" ? colors.sky[400] : colors.sky[600];
  const seriesHoverColor = theme === "dark" ? colors.sky[500] : colors.sky[600];

  const periods = [
    {
      period: ChartPeriodId.P1M,
      label: intl.formatMessage({ defaultMessage: "1M", id: "1uz/I3" }),
      tooltipContent: intl.formatMessage({ defaultMessage: "1 mês", id: "T0sgAr" }),
      from: subMonths(new Date(), 1),
    },
    {
      period: ChartPeriodId.P3M,
      label: intl.formatMessage({ defaultMessage: "3M", id: "tOR9ZQ" }),
      tooltipContent: intl.formatMessage({ defaultMessage: "3 meses", id: "nc1Tv9" }),
      from: subMonths(new Date(), 3),
    },
    {
      period: ChartPeriodId.P6M,
      label: intl.formatMessage({ defaultMessage: "6M", id: "LZqvbG" }),
      tooltipContent: intl.formatMessage({ defaultMessage: "6 meses", id: "fECWsi" }),
      from: subMonths(new Date(), 6),
    },
    {
      period: ChartPeriodId.P1Y,
      label: intl.formatMessage({ defaultMessage: "1A", id: "L7Z4zK" }),
      tooltipContent: intl.formatMessage({ defaultMessage: "1 ano", id: "Lc4l9T" }),
      from: subYears(new Date(), 1),
    },
  ];
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const period = periods.find(({ period }) => period === ChartPeriodId.P1M);
    if (!period) {
      throw new Error("Couldn't locate the default period");
    }
    return {
      period: period.period,
      startDate: period.from,
      endDate: new Date(),
    };
  });

  const tickerSymbolsForSeries = [...tickerSymbols];
  const { data, previousData, loading } = useQuery<IQuotesHistoryQuery, IQuotesHistoryQueryVariables>(
    QuotesHistoryQuery,
    {
      variables: {
        tickerSymbols: [...new Set(tickerSymbolsForSeries)].slice(0, 5),
        startDate: lightFormat(selectedPeriod.startDate, "yyyy-MM-dd"),
        endDate: lightFormat(selectedPeriod.endDate, "yyyy-MM-dd"),
      },
    },
  );

  const isShortPeriod = [ChartPeriodId.P6M, ChartPeriodId.P1Y].includes(selectedPeriod.period);
  const quotesHistory = (loading && !data ? previousData?.quotesHistory : data?.quotesHistory) || [];
  const hasQuotesHistory = quotesHistory.length > 0;

  const primaryValues = (quotesHistory[0]?.nodes ?? [])
    .filter((node) => (isAmericanCompany ? node.volumeTraded > 0 : node.numberOfTrades > 0))
    .map((node) => (isAmericanCompany ? node.volumeTraded : node.numberOfTrades));
  const meanValue = calculateMean(primaryValues);
  const medianValue = calculateMedian(primaryValues);

  const compactFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const series: SeriesColumnOptions[] = quotesHistory.map((series, index) => ({
    showInNavigator: index === 0,
    id: series.tickerSymbol,
    type: "column",
    animation: false,
    name: series.tickerSymbol,
    color: isShortPeriod ? seriesColorShortPeriod : seriesColor,
    borderColor: seriesBorderColor,
    borderWidth: isShortPeriod ? 0 : 1.5,
    data: series.nodes
      .filter((node) => {
        if (isAmericanCompany) {
          return node.volumeTraded > 0;
        }

        return node.numberOfTrades > 0;
      })
      .map((node) => ({
        x: parseISO(node.date).getTime(),
        y: isAmericanCompany ? node.volumeTraded : node.numberOfTrades,
        custom: node,
      }))
      .sort((a, b) => compareAsc(a.x, b.x)),
    custom: {
      tickerSymbol: series.tickerSymbol,
      primaryColor: seriesColor,
      secondaryColor: seriesBorderColor,
    },
  }));

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
      events: {
        render() {
          const chart = this as Highcharts.Chart;

          const plotLines: YAxisPlotLinesOptions[] | undefined = flatten(
            quotesHistory.map((node, index) => {
              const series = chart.series.find((serie) => serie.name === node.tickerSymbol);

              if (!series) {
                return null;
              }

              const colorTheme: ColorTheme = getColorThemeForIndex(index, theme);

              if (chart.yAxis.length > 0) {
                chart.yAxis[0].removePlotLine(`median`);
              }

              const values = series.points.map((node) => node.y).filter(notUndefinedOrNull);

              const sortedValues = [...values].sort((a, b) => a - b);
              const mid = Math.floor(sortedValues.length / 2);
              const medianVal =
                sortedValues.length % 2 !== 0
                  ? sortedValues[mid]
                  : ((sortedValues[mid - 1] ?? 0) + (sortedValues[mid] ?? 0)) / 2;

              return [
                {
                  id: `median`,
                  color: colorTheme.lineColor,
                  value: medianVal,
                  dashStyle: "Dash" as DashStyleValue,
                  width: 1,
                  zIndex: 4,
                },
              ];
            }),
          ).filter(notUndefinedOrNull);

          if (plotLines && chart.yAxis.length > 0) {
            plotLines.forEach((node) => {
              chart.yAxis[0].addPlotLine(node);
            });
          }
        },
      },
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    yAxis: {
      scrollbar: {
        enabled: false,
      },
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        // eslint-disable-next-line
        formatter: function () {
          const valueFormatter = new Intl.NumberFormat("en", {
            notation: "compact",
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          });
          const rawValue = parseFloat(String(this.value));
          if (rawValue !== 0) {
            return valueFormatter.format(rawValue);
          }
          return "";
        },
      },
      tickWidth: 0,
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as QuoteChartNode;

        if (!this.key || !node || node.__typename !== "QuoteChartNode") {
          return "";
        }

        return renderToStaticMarkup(
          <KeyValueTooltip
            x={intl.formatDate(parseISO(node.date), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
            keyValues={createTooltipKeyValues(issuer, intl, node, theme)}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: theme === "dark" ? colors.slate[400] : colors.slate[500],
            lineWidth: 2,
          },
        },
      },
      column: {
        states: {
          hover: {
            color: seriesHoverColor,
            borderColor: seriesHoverColor,
          },
        },
      },
    },
  };

  if (loading && !hasQuotesHistory) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            )}

            <ChartPeriodSelector
              periods={periods}
              value={selectedPeriod.period}
              onChange={(period, startDate, endDate) => {
                setSelectedPeriod({
                  period,
                  startDate,
                  endDate,
                });
              }}
            />
          </div>

          {hasQuotesHistory && primaryValues.length > 0 && (
            <div className="hidden items-center space-x-3 sm:inline-flex">
              <div className="flex flex-col items-end">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-400">
                  <FormattedMessage defaultMessage="Mediana" id="fjhEa/" />
                </span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-200">
                  {compactFormatter.format(medianValue)}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-400">
                  <FormattedMessage defaultMessage="Média" id="LbWBXi" />
                </span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-200">
                  {compactFormatter.format(meanValue)}
                </span>
              </div>
            </div>
          )}
        </div>

        {hasQuotesHistory ? (
          <HighchartsChart options={options} className="h-96 xl:h-full" />
        ) : (
          <div className="flex h-96 w-80 items-center justify-center sm:w-full">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}
      </div>
    </OnlyPremiumRender>
  );
}

type QuoteChartNode = IQuotesHistoryQuery["quotesHistory"]["0"]["nodes"][number] | null | undefined;

function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function calculateMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? (sorted[mid] ?? 0) : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function createTooltipKeyValues(
  issuer:
    | IAmericanCompanyIssuerTopLevelFragment
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment,
  intl: IntlShape,
  node: NonNullable<QuoteChartNode>,
  currentTheme: "dark" | "light",
) {
  const isFiis = issuer.__typename === "BrazilianRealEstateFund";
  const labelColor = currentTheme === "dark" ? colors.sky[400] : colors.sky[600];

  const labels = {
    numberOfTrades: intl.formatMessage({
      defaultMessage: "Negócios",
      id: "angATp",
    }),
    volume: isFiis
      ? intl.formatMessage({
          defaultMessage: "Cotas Negociadas",
          id: "MlqwgY",
        })
      : intl.formatMessage({
          defaultMessage: "Ações Negociadas",
          id: "6oJmci",
        }),
    financialVolume: intl.formatMessage({
      defaultMessage: "Volume Financeiro",
      id: "lLfwLk",
    }),
  };

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const moneyFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  if (issuer.__typename === "AmericanCompanyIssuer") {
    return [
      {
        key: labels.volume,
        value: valueFormatter.format(node.volumeTraded),
        color: labelColor,
      },
    ];
  }

  return [
    {
      key: labels.numberOfTrades,
      value: valueFormatter.format(node.numberOfTrades),
      color: labelColor,
    },
    {
      key: labels.volume,
      value: valueFormatter.format(node.volumeTraded),
    },
    {
      key: labels.financialVolume,
      value: moneyFormatter.format(node.financialVolume),
    },
  ];
}
